import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { Sequence } from '../../types/campaign';
import { SequenceStatusBadge } from '../../components/Sequences/SequenceStatusBadge';
import { SequenceActionButtons } from '../../components/Sequences/SequenceActionButtons';
import { SequenceFilters } from '../../components/Sequences/SequenceFilters';
import { SequenceEmptyState } from '../../components/Sequences/SequenceEmptyState';
import { SequenceLoadingSkeleton } from '../../components/Sequences/SequenceLoadingSkeleton';

export const SequencesListPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Fetch sequences
  const { data: sequences = [], isLoading, error } = useQuery<Sequence[]>({
    queryKey: ['sequences', statusFilter],
    queryFn: async () => {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      const response = await api.sequences.list(params);
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    },
  });

  // Filter sequences by search query
  const filteredSequences = sequences.filter((sequence) =>
    sequence.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate total duration for a sequence
  const calculateTotalDuration = (sequence: Sequence): string => {
    if (!sequence.steps || sequence.steps.length === 0) {
      return '0 days';
    }

    let totalHours = 0;
    let totalDays = 0;

    sequence.steps.forEach((step) => {
      if (step.delay_hours) {
        totalHours += step.delay_hours;
      }
      if (step.delay_days) {
        totalDays += step.delay_days;
      }
    });

    // Convert hours to days if >= 24
    if (totalHours >= 24) {
      totalDays += Math.floor(totalHours / 24);
      totalHours = totalHours % 24;
    }

    const parts: string[] = [];
    if (totalDays > 0) {
      parts.push(`${totalDays} day${totalDays !== 1 ? 's' : ''}`);
    }
    if (totalHours > 0) {
      parts.push(`${totalHours} hour${totalHours !== 1 ? 's' : ''}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'Immediate';
  };

  // Action handlers
  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sequences</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage multi-step message sequences for your campaigns
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate('/sequences/new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create Sequence
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6">
        <SequenceFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onClearFilters={handleClearFilters}
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="mt-8">
          <SequenceLoadingSkeleton type="table" count={5} />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mt-8">
          <SequenceEmptyState
            type="error"
            errorMessage={(error as any).message || 'An unexpected error occurred'}
          />
        </div>
      )}

      {/* Empty State - No Sequences */}
      {!isLoading && !error && sequences.length === 0 && !statusFilter && !searchQuery && (
        <div className="mt-8">
          <SequenceEmptyState
            type="no-sequences"
            onCreateSequence={() => navigate('/sequences/new')}
          />
        </div>
      )}

      {/* Empty State - No Results */}
      {!isLoading && !error && filteredSequences.length === 0 && (statusFilter || searchQuery) && (
        <div className="mt-8">
          <SequenceEmptyState
            type="no-results"
            onClearFilters={handleClearFilters}
          />
        </div>
      )}

      {/* Sequences Table */}
      {!isLoading && !error && filteredSequences.length > 0 && (
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Steps
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Duration
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Last Updated
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredSequences.map((sequence) => (
                      <tr
                        key={sequence.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/sequences/${sequence.id}`)}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">{sequence.name}</div>
                          {sequence.description && (
                            <div className="text-gray-500 truncate max-w-xs">{sequence.description}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <SequenceStatusBadge status={sequence.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {sequence.steps?.length || 0} step{(sequence.steps?.length || 0) !== 1 ? 's' : ''}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {calculateTotalDuration(sequence)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(sequence.updated_at)}
                        </td>
                        <td
                          className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SequenceActionButtons sequence={sequence} size="md" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

